import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { fetchProductsBySellerId } from "../../../State/customer/productSlice";
import StorefrontIcon from "@mui/icons-material/Storefront";
import StarIcon from "@mui/icons-material/Star";
import { teal } from "@mui/material/colors";

const SellerProducts: React.FC = () => {
  const { sellerId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Access Redux state
  const { sellerProducts, product, loading } = useAppSelector((state) => state.product);

  useEffect(() => {
    if (sellerId) {
      dispatch(fetchProductsBySellerId(Number(sellerId)));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [sellerId, dispatch]);

  // Ensure products list is always an array regardless of API response wrapping
  const productsList = Array.isArray(sellerProducts)
    ? sellerProducts
    : (sellerProducts as any)?.content || [];

  // Extract seller information from the first fetched seller product or fallback to current selected product
  const firstProduct = productsList[0];
  const sellerObj = firstProduct?.seller || product?.seller;

  const sellerName =
    sellerObj?.sellerName ||
    sellerObj?.businessDetails?.businessName ||
    "Verified Vendor";

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] text-teal-600 font-medium">
        Loading seller store...
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 lg:px-20 py-8 max-w-7xl mx-auto">
      {/* Banner / Store Header */}
      <div className="bg-gray-100 border border-gray-200 text-gray-800 rounded-xl p-6 sm:p-10 mb-8 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-4 bg-teal-600/10 text-teal-700 rounded-full">
            <StorefrontIcon sx={{ fontSize: 40 }} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{sellerName}</h1>
            <p className="text-gray-600 text-sm mt-1">
              Verified Marketplace Seller • {productsList.length} Products Available
            </p>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <h2 className="text-xl font-bold text-gray-800 mb-6">
        Products from this Store
      </h2>

      {productsList.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500 font-medium">
            No products found for this vendor.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
          {productsList.map((item: any) => (
            <div
              key={item.id}
              onClick={() =>
                navigate(
                  `/product-details/${item.category?.categoryId || item.category?.id || 1}/${encodeURIComponent(item.title)}/${item.id}`
                )
              }
              className="group cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div>
                <div className="w-full h-44 sm:h-52 overflow-hidden rounded-md bg-gray-100">
                  <img
                    src={item.images?.[0] || "https://via.placeholder.com/300"}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <h3 className="mt-3 font-semibold text-gray-800 text-sm line-clamp-1 group-hover:text-teal-600 transition-colors">
                  {item.title}
                </h3>
                <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                  <StarIcon sx={{ color: teal[600], fontSize: "16px" }} />
                  <span>4.0</span>
                </div>
              </div>

              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-bold text-gray-900 text-sm sm:text-base">
                  Ksh {item.sellingPrice?.toLocaleString()}
                </span>
                {item.mrpPrice > item.sellingPrice && (
                  <span className="line-through text-xs text-gray-400">
                    Ksh {item.mrpPrice?.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SellerProducts;